import React, { createContext, useState, useRef, useContext, useEffect, useCallback } from 'react';

const AudioContext = createContext();

const getMediaErrorReason = (mediaError) => {
  switch (mediaError?.code) {
    case 1:
      return 'playback_aborted';
    case 2:
      return 'network_error';
    case 3:
      return 'decode_error';
    case 4:
      return 'audio_not_found_or_format_not_supported';
    default:
      return 'unknown_audio_error';
  }
};

export const useAudio = () => {
  return useContext(AudioContext);
};

export const AudioProvider = ({ children }) => {
  const [currentAudio, setCurrentAudio] = useState(null);
  const isPlayingRef = useRef(false);
  const audioRef = useRef(null);

  const playSound = async (audioUrl) => {
    // Don't try to play if no URL provided
    if (!audioUrl) {
      console.warn('⚠️ [Audio] Cannot play sound because no audio URL was provided.');
      return;
    }

    if (audioRef.current && audioRef.current.src === audioUrl) {

      if (isPlayingRef.current) {
        audioRef.current.pause();
        isPlayingRef.current = false;
      } else {
        audioRef.current.currentTime = 0; // Reset the current time to the start
        try {
          await audioRef.current.play();
          isPlayingRef.current = true;
        } catch (error) {
          console.warn('Audio playback failed:', error.message);
          isPlayingRef.current = false;
        }
      }
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeEventListener('ended', handleAudioEnded);
      audioRef.current.removeEventListener('error', handleAudioError);
    }

    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    audio.addEventListener('ended', handleAudioEnded);
    audio.addEventListener('error', handleAudioError);

    try {
      await audio.play();
      setCurrentAudio(audioUrl);
      isPlayingRef.current = true;
    } catch (error) {
      console.warn('Audio playback failed:', error.message);
      isPlayingRef.current = false;
      setCurrentAudio(null);
    }
  };

  const handleAudioError = useCallback((e) => {
    const failedAudioUrl = e?.target?.currentSrc || e?.target?.src || currentAudio;
    console.error('❌ [Audio] Failed to load audio asset.', {
      audioUrl: failedAudioUrl,
      reason: getMediaErrorReason(e?.target?.error),
      mediaErrorCode: e?.target?.error?.code || null,
      message: e?.target?.error?.message || 'Failed to load audio'
    });
    isPlayingRef.current = false;
    setCurrentAudio(null);
  }, [currentAudio]);

  const handleAudioEnded = useCallback(() => {
    isPlayingRef.current = false;
  }, []);

  const stopSound = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeEventListener('ended', handleAudioEnded);
      audioRef.current.removeEventListener('error', handleAudioError);
      audioRef.current = null;
    }
    setCurrentAudio(null);
    isPlayingRef.current = false;
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeEventListener('ended', handleAudioEnded);
        audioRef.current.removeEventListener('error', handleAudioError);
      }
    };
  }, [handleAudioEnded, handleAudioError]);

  const value = {
    currentAudio,
    isPlayingRef,
    playSound,
    stopSound,
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};
